import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
} from 'react';
import useTypedPage from '@/Hooks/useTypedPage';
import axios from 'axios';
import useRoute from '@/Hooks/useRoute';
import BillingModal from '@/Components/Billing/BillingModal';
import { getStorage, setStorage } from '@/utils/LocalStorageManager';
import { useServerSentEvents } from '@/Contexts/SSEProvider';

export interface ModeContextProps {
  error: string | null;
  mode: App.Data.ModeData | null;
  preset: App.Data.PresetData | null;
  modes: App.Data.ModeData[];
  presets: App.Data.PresetData[];
  setMode: Function;
  setPreset: Function;
}

interface ModeProviderProps {
  children: React.ReactNode;
}

function setModeInLocalStorage(mode: App.Data.ModeData) {
  setStorage('currentMode', mode.id.toString());
}

function getModeFromLocalStorage(modes: App.Data.ModeData[]) {
  const storedMode = getStorage('currentMode');
  const storedModeId = storedMode ? parseInt(storedMode) : null;
  return modes.find(mode => mode.id === storedModeId);
}

export const ModeContext = createContext<ModeContextProps>({
  error: null,
  mode: null,
  preset: null,
  modes: [],
  presets: [],
  setMode: () => {},
  setPreset: () => {},
});

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error(`useMode must be used within a ModeProvider`);
  }
  return context;
};

export const ModeProvider: FC<ModeProviderProps> = ({ children }) => {
  const { modes, file, conversation, auth, chats } = useTypedPage<{
    modes: App.Data.ModeData[];
    file: App.Data.FileData;
    conversation: App.Data.ConversationData;
    chats: App.Data.ChatData[];
  }>().props;
  const plan = auth?.user?.current_team?.billing.plan;
  const route = useRoute();
  const [error, setError] = useState<string | null>(null);
  const { events } = useServerSentEvents();
  const [mode, setModeState] = useState<App.Data.ModeData | null>(
    getModeFromLocalStorage(modes) ??
      conversation?.mode ??
      file?.mode ??
      modes[0]
  );
  const [preset, setPreset] = useState<App.Data.PresetData | null>();
  const [presets, setPresets] = useState<App.Data.PresetData[]>([]);
  const [open, setOpen] = useState<App.Data.ModeData | null>(null);
  const proxied = useTypedPage<{ proxied: boolean }>().props.proxied;

  async function getPresets(
    mode: App.Data.ModeData
  ): Promise<App.Data.PresetData[]> {
    const response = await axios.get(
      route('api.modes.show', { mode: mode.id })
    );
    return response.data.presets as App.Data.PresetData[];
  }

  function setMode(newMode: App.Data.ModeData) {
    setModeState(newMode);
    setModeInLocalStorage(newMode);
    setPreset(null); // Reset preset when mode changes
  }

  /**
   * Exit Presets when in preset mode
   * and a create event occurs.
   */
  useEffect(() => {
    if (preset && events.get('create')) {
      setPreset(null);
    }
  }, [events, preset]);

  useEffect(() => {
    async function fetchPresets(mode: App.Data.ModeData) {
      try {
        const fetchedPresets = await getPresets(mode);
        const preset = file?.package?.preset ?? null;
        setPreset(preset);
        setPresets(fetchedPresets);
      } catch (error: any) {
        setError(error?.message);
      }
    }

    if (mode && !proxied) {
      fetchPresets(mode);
    }
  }, [mode]);

  useEffect(() => {
    if (plan === 'free' && mode?.plan === 'pro') {
      setMode(modes[0]);
      setOpen(mode);
    }
  }, [mode, plan, modes]);

  return (
    <ModeContext.Provider
      value={{
        mode,
        preset,
        setMode,
        setPreset,
        modes,
        presets,
        error,
      }}
    >
      {children}
      <BillingModal
        open={!!open}
        close={() => setOpen(null)}
        headline={
          open
            ? `${open.name} is a pro mode, you must upgrade your plan to gain access.`
            : "Advanced features and no hidden pricing. Try it out for free and upgrade when you're ready."
        }
      />
    </ModeContext.Provider>
  );
};
